import React, { useState } from 'react';
import { Download, Rocket } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHAPES = [
  { key: 'portrait', label: 'Portrait 9:16', help: 'Reels & Stories' },
  { key: 'square', label: 'Square 1:1', help: 'Feeds' },
];
const PART_LABELS = { problem: 'Problem', agitation: 'Why it hurts', solution: 'Solution', benefits: 'Benefits', cta: 'Call to action' };

/** Watch and download the video version of an ad, in both shapes, with its script. */
export default function VideoAdDialog({ variant, open, onOpenChange, onLaunch }) {
  const [shape, setShape] = useState('portrait');
  const url = variant?.videos?.[shape];
  const src = url ? (url.startsWith('http') ? url : `${BACKEND_URL}${url}`) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="video-dialog">
        <DialogHeader>
          <DialogTitle>Your video ad</DialogTitle>
          <DialogDescription>
            {variant?.video_style === 'sora' ? 'A 12-second AI video made from your photo'
              : variant?.video_style === 'still' ? 'Your ad’s key points on one image' : 'Scene by scene with subtitles'}
            {variant?.video_voice ? ', with a voiceover.' : ' (the voiceover couldn’t be made this time).'} Download it,
            or make a new one if you'd like a different take.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 mb-3">
          {SHAPES.map(s => (
            <button key={s.key} onClick={() => setShape(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs border-2 ${shape === s.key ? 'border-ink bg-butter font-semibold' : 'border-ink/20 text-muted-foreground'}`}>
              {s.label} <span className="opacity-70">· {s.help}</span>
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-[auto,1fr] gap-4 items-start">
          {src && (
            <video key={src} src={src} controls playsInline
              className={`rounded-xl bg-black mx-auto ${shape === 'portrait' ? 'w-56 aspect-[9/16]' : 'w-full max-w-72 aspect-square'}`} />
          )}
          <div className="space-y-3">
            {src && onLaunch && (
              <button onClick={() => onLaunch(shape)} data-testid="video-launch"
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg gradient-bg text-sm">
                <Rocket className="w-4 h-4" /> Launch with this video
              </button>
            )}
            {src && (
              <a href={src} download={`rayogent-video-${shape}.mp4`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-ink text-sm font-medium hover:bg-butter" data-testid="video-download">
                <Download className="w-4 h-4" /> Download {shape === 'portrait' ? '9:16' : '1:1'} video
              </a>
            )}
            <ol className="space-y-2 text-sm">
              {(variant?.video_script || []).map((s, i) => (
                <li key={i} className="p-2 rounded-lg bg-butter/50 border-2 border-ink/15">
                  <span className="text-[10px] uppercase tracking-wide text-brand-strong">{PART_LABELS[s.part] || s.part}</span>
                  <p className="font-medium">{s.headline}</p>
                  <p className="text-xs text-muted-foreground">{s.subtitle}</p>
                  {s.bullets?.length > 0 && <p className="text-xs text-muted-foreground">{s.bullets.join(' · ')}</p>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
